import { MentoringProgramMatchingType, MentorshipProgramAliases, TargetAudience } from 'redux/schemas/models/mentoring-programs';
import { CustomCommunicationMessageType } from 'redux/schemas/models/mentorship-program-custom-communications';

type DefaultTemplate = {
  subject: string;
  body: string;
};

/**
 * Extracts HTML string from a translation function that returns a React element with dangerouslySetInnerHTML
 */
const extractHtmlFromTranslation = (translationResult: any): string => {
  // If it's already a string, return it
  if (typeof translationResult === 'string') {
    return translationResult;
  }

  // If it's a React element with dangerouslySetInnerHTML, extract the HTML
  if (translationResult?.props?.dangerouslySetInnerHTML?.__html) {
    return translationResult.props.dangerouslySetInnerHTML.__html;
  }

  // Fallback to empty string
  return '';
};

/**
 * Gets the default template for a communication based on matching type, message type, and target audience.
 * For AUTO matching, all audiences share the same template (stored under MENTOR_AND_MENTEE).
 * For PARTICIPANT_DRIVEN, templates differ for MENTOR vs MENTEE.
 */
export const getDefaultTemplate = (
  t: any,
  matchingType: MentoringProgramMatchingType,
  messageType: CustomCommunicationMessageType,
  targetAudience: TargetAudience,
  mentoringProgramAliases: MentorshipProgramAliases,
): DefaultTemplate | null => {
  const templates = t.MENTORING_PROGRAMS.COMMUNICATIONS.COMMUNICATION_MODAL.ADD_MODE.DEFAULT_CUSTOM_TEMPLATES;

  // Determine the matching type key
  const matchingTypeKey = matchingType === MentoringProgramMatchingType.AUTOMATED_MATCHING
    ? 'AUTO'
    : 'PARTICIPANT_DRIVEN';

  const matchingTypeTemplates = templates[matchingTypeKey];
  if (!matchingTypeTemplates) return null;

  // Convert enum values to uppercase for translation keys
  const messageTypeKey = messageType.toUpperCase();
  const targetAudienceKey = targetAudience.toUpperCase();

  // Get message type templates
  const messageTypeTemplates = matchingTypeTemplates[messageTypeKey];
  if (!messageTypeTemplates) return null;

  // For AUTO matching, always use MENTOR_AND_MENTEE template regardless of actual target audience
  if (matchingType === MentoringProgramMatchingType.AUTOMATED_MATCHING) {
    const template = messageTypeTemplates.MENTOR_AND_MENTEE;
    if (!template) return null;
    return {
      subject: template.SUBJECT(),
      body: extractHtmlFromTranslation(template.BODY(mentoringProgramAliases)),
    };
  }

  // For PARTICIPANT_DRIVEN, check if MENTOR_AND_MENTEE template exists
  // If target is MENTOR_AND_MENTEE and template exists, use it
  // Otherwise, use role-specific template
  if (targetAudience === TargetAudience.MENTOR_AND_MENTEE) {
    const mentorAndMenteeTemplate = messageTypeTemplates.MENTOR_AND_MENTEE;
    if (mentorAndMenteeTemplate) {
      return {
        subject: mentorAndMenteeTemplate.SUBJECT(),
        body: extractHtmlFromTranslation(mentorAndMenteeTemplate.BODY(mentoringProgramAliases)),
      };
    }
    // If no unified template exists, return null (they need to choose a specific role)
    return null;
  }

  const audienceTemplate = messageTypeTemplates[targetAudienceKey];
  if (!audienceTemplate) return null;

  return {
    subject: audienceTemplate.SUBJECT(),
    body: extractHtmlFromTranslation(audienceTemplate.BODY(mentoringProgramAliases)),
  };
};
